import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthContext";

/**
 * Protected Route: Only users with specified roles can access
 * Uses user state from AuthContext to avoid duplicate authentication checks
 *
 * @param {string} requiredRole Required role, such as "admin" or "parent"
 * @param {ReactNode} children
 */
export default function ProtectedRoute({ requiredRole, children }) {
  const { user, loading } = useAuth();

  // If loading, show loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Verifying identity...</p>
        </div>
      </div>
    );
  }

  // Role check helper (supports teacher via isTeacher flag)
  const hasRequiredRole = (() => {
    if (!user) return false;
    if (requiredRole === "admin") return user.role === "admin";
    if (requiredRole === "parent") return user.role === "parent";
    if (requiredRole === "teacher") return user.role === "teacher" || user.isTeacher === true;
    return false;
  })();

  // If no user or user role doesn't match, redirect to login page
  if (!user || !hasRequiredRole) {
    console.log(`[ProtectedRoute] Access denied:`, {
      user: user ? { role: user.role, email: user.email, isTeacher: user.isTeacher } : null,
      requiredRole,
      redirecting: true
    });
    return <Navigate to="/login" replace />;
  }

  // User authenticated and role matches, render protected content
  console.log(`[ProtectedRoute] Access granted:`, {
    user: { role: user.role, email: user.email, isTeacher: user.isTeacher },
    requiredRole
  });
  
  return children;
}
